<template>
  <div class="form-row">
    <div
      class="text-center col-md-12 pl-2 pr-2"
      v-show="vehicleFleetData.length > 0 || addressCoordinates"
    >
      <GmapMap
        ref="mapRef"
        :center="computedCenter"
        :zoom="currentZoom"
        style="width: 100%; height: 700px"
        @zoom_changed="handleZoomChanged"
      >
        <GmapMarker
          v-if="addressCoordinates && addressCoordinates.lat && addressCoordinates.lng"
          :position="{ lat: Number(addressCoordinates.lat), lng: Number(addressCoordinates.lng) }"
          :icon="getAddressIcon()"
        />
        <GmapMarker
          v-for="(vehicle, index) in vehicleFleetData"
          :key="'vehicle-' + getVehicleKey(vehicle)"
          v-if="vehicle.position && getCoord(vehicle.position)"
          :position="getCoord(vehicle.position)"
          :icon="getVehicleMarkerIcon(vehicle)"
          @click="setSelectedVehicleDetails(vehicle)"
        />

        <GmapMarker
          v-for="(landmark, idx) in landmarkMarkers"
          :key="'landmark-' + landmark.id + '-' + idx"
          v-if="landmark.destino_lat && landmark.destino_long"
          :position="{ lat: Number(landmark.destino_lat), lng: Number(landmark.destino_long) }"
          :icon="getLandmarkIcon(landmark)"
          @click="onLandmarkMarkerClick(landmark)"
        />

        <GmapInfoWindow
          v-if="showInfoWindow === 'landmark' && selectedLandmark"
          :position="{ lat: Number(selectedLandmark.destino_lat), lng: Number(selectedLandmark.destino_long) }"
          @closeclick="closeInfoWindow"
        >
          <div class="landmark-info">
            <h6 class="mb-2">{{ selectedLandmark.destino_nome || selectedLandmark.name }}</h6>
            <p class="mb-1" v-if="selectedLandmark.landmark_type && selectedLandmark.landmark_type.name">
              <span class="badge badge-info">{{ selectedLandmark.landmark_type.name }}</span>
            </p>
            <p class="mb-0 text-muted small">
              {{ Number(selectedLandmark.destino_lat).toFixed(6) }},
              {{ Number(selectedLandmark.destino_long).toFixed(6) }}
            </p>
          </div>
        </GmapInfoWindow>

        <GmapCircle
          v-if="addressCoordinates && normalizedRadius && normalizedRadius > 0"
          :key="
            'circle-' +
            normalizedRadius +
            '-' +
            addressCoordinates.lat +
            '-' +
            addressCoordinates.lng
          "
          :center="addressCoordinates"
          :radius="normalizedRadius * 1000"
          :options="getCircleOptions()"
          @bounds_changed="onCircleChanged"
        />

        <GmapInfoWindow
          v-if="showInfoWindow === 'vehicle'"
          :position="markerPosition"
          @closeclick="closeInfoWindow"
        >
          <div>
            <vehicle-info
              :vehicle="selectedVehicleDetails"
              :vehicleDatalhe="vehicleDatalhe"
            />
          </div>
        </GmapInfoWindow>

        <GmapInfoWindow
          v-if="showInfoWindow === 'address'"
          :position="addressCoordinates"
          @closeclick="closeInfoWindow"
        >
          <div class="address-info">
            <h6 class="mb-2">📍 {{ $t("Reference Address") }}</h6>
            <p class="mb-1">
              <strong>{{ addressCoordinates.formatted_address }}</strong>
            </p>
            <p class="mb-1" v-if="normalizedRadius">
              <span class="badge badge-primary"
                >{{ $t("Radius") }}: {{ normalizedRadius }} km</span
              >
            </p>
            <p class="mb-0 text-muted small">
              {{ addressCoordinates.lat.toFixed(6) }},
              {{ addressCoordinates.lng.toFixed(6) }}
            </p>
          </div>
        </GmapInfoWindow>
      </GmapMap>
    </div>

    <div
      class="form-group col-md-12 pl-2 pr-2 text-center"
      v-show="vehicleFleetData.length === 0 && !addressCoordinates"
    >
      <p class="badge badge-warning">{{ $t("location not specified") }}</p>
      <CurrentLocationMap />
    </div>
  </div>
</template>

<script>
import { RefreshCcwIcon } from "vue-feather-icons";
import CurrentLocationMap from "./CurrentLocationMap";
import VehicleInfo from "./VehicleInfo";

export default {
  props: {
    vehicleFleet: {
      type: Array,
      default: () => [],
    },
    landmarkMarkers: {
      type: Array,
      default: () => [],
    },
    zoomMapa: {
      type: Number,
      default: 13,
    },
    selectedVehicleId: {
      type: Number,
      default: null,
    },
    addressCoordinates: {
      type: Object,
      default: null,
    },
    radiusKm: {
      type: [Number, String],
      default: null,
      validator(value) {
        if (value === null || value === undefined) return true;
        const num = Number(value);
        return !isNaN(num) && num > 0;
      },
    },
    autoFitMap: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      latitude: 0,
      longitude: 0,
      showInfoWindow: null,
      selectedVehicleDetails: null,
      vehicleDatalhe: null,
      selectedLandmark: {},
      circleRadius: 1000,
      indexMarker: null,
      markerPosition: {
        lat: 0,
        lng: 0,
      },
      // Centro padrão de fallback (São Paulo)
      center: {
        lat: -23.6086,
        lng: -46.6663,
      },
      currentZoom: 13,
      mapViewTimer: null,
    };
  },

  computed: {
    vehicleFleetData() {
      return this.vehicleFleet ?? [];
    },

    /**
     * Centro calculado dinamicamente:
     * 1. Se há addressCoordinates, usa ele
     * 2. Se há pins (veículos + landmarks), calcula o centróide
     * 3. Fallback: São Paulo
     */
    computedCenter() {
      if (this.addressCoordinates) {
        return this.addressCoordinates;
      }

      const points = [];

      this.vehicleFleetData.forEach((vehicle) => {
        const coord = this.getCoord(vehicle.position);
        if (coord) points.push(coord);
      });

      this.landmarkMarkers.forEach((landmark) => {
        const lat = Number(landmark?.destino_lat);
        const lng = Number(landmark?.destino_long);
        if (!isNaN(lat) && !isNaN(lng)) points.push({ lat, lng });
      });

      if (points.length > 0) {
        const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
        const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
        return { lat: avgLat, lng: avgLng };
      }

      return this.center;
    },

    normalizedRadius() {
      if (!this.radiusKm) return null;
      return Number(this.radiusKm);
    },
  },

  watch: {
    vehicleFleetData: {
      handler(newData) {
        if (newData && newData.length > 0) {
          this.scheduleMapViewUpdate();
        }
      },
      immediate: true,
      deep: true,
    },

    selectedVehicleId: {
      handler(newVehicleId) {
        if (newVehicleId) {
          this.focusOnSelectedVehicle();
        } else {
          this.scheduleMapViewUpdate();
        }
      },
      immediate: true,
    },

    zoomMapa: {
      handler(newZoom) {
        this.currentZoom = newZoom;
      },
      immediate: true,
    },

    addressCoordinates: {
      handler(newCoords, oldCoords) {
        if (newCoords && this.normalizedRadius) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.fitMapToCircle();
            }, 800);
          });
        } else if (newCoords) {
          this.updateMapViewForAddress();
          this.showInfoWindow = "address";
        } else {
          this.scheduleMapViewUpdate();
        }
      },
      immediate: false,
      deep: true,
    },

    landmarkMarkers: {
      handler() {
        this.scheduleMapViewUpdate();
      },
      deep: true,
      immediate: true,
    },

    normalizedRadius: {
      handler(newRadius, oldRadius) {
        if (newRadius && this.addressCoordinates) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.fitMapToCircle();
            }, 800);
          });
        }
      },
      immediate: false,
    },
  },

  methods: {
    scheduleMapViewUpdate(delay = 120) {
      if (this.mapViewTimer) {
        clearTimeout(this.mapViewTimer);
      }

      this.$nextTick(() => {
        this.mapViewTimer = setTimeout(() => {
          this.mapViewTimer = null;
          this.updateMapView();
        }, delay);
      });
    },

    getVehicleMarkerIcon(vehicle) {
      let iconUrl = vehicle?.position?.icon;
      if (typeof iconUrl !== "string" || !iconUrl.trim()) {
        iconUrl = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
      }
      return {
        url: iconUrl,
        scaledSize: { width: 40, height: 60 },
      };
    },

    getVehicleKey(vehicle) {
      return (
        vehicle?.vehicle_id ||
        vehicle?.vehicle?.id ||
        vehicle?.id ||
        `${vehicle?.position?.lat || 0}-${vehicle?.position?.lng || 0}`
      );
    },

    getLandmarkIcon(landmark) {
      let iconUrl = landmark?.landmark_type?.file;
      if (typeof iconUrl !== "string" || !iconUrl.trim()) {
        iconUrl = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
      }
      return {
        url: iconUrl,
        scaledSize: { width: 30, height: 45 },
      };
    },

    onLandmarkMarkerClick(landmark) {
      this.selectedLandmark = landmark;
      this.showInfoWindow = "landmark";
    },

    closeInfoWindow() {
      this.showInfoWindow = null;
      this.selectedVehicleDetails = null;
      this.selectedLandmark = {};
    },

    getVehicleIcon(vehicle) {
      if (vehicle.vehicle_image_url) {
        return {
          url: vehicle.vehicle_image_url,
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16),
        };
      }

      return {
        url: require("@/assets/images/icons/ambulance.png"),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
        origin: new google.maps.Point(0, 0),
      };
    },

    getAddressIcon() {
      return {
        url: require("@/assets/images/pin/map-pins-rescue.png"),
        scaledSize: new google.maps.Size(32, 40),
        anchor: new google.maps.Point(16, 40),
        origin: new google.maps.Point(0, 0),
      };
    },

    getCircleOptions() {
      return {
        strokeColor: "#4285F4",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#4285F4",
        fillOpacity: 0.15,
        clickable: false,
        zIndex: 1,
      };
    },

    showAddressInfo() {
      this.showInfoWindow = null;
      this.$nextTick(() => {
        this.showInfoWindow = "address";
      });
    },

    getMapPoints() {
      const points = [];

      this.vehicleFleetData.forEach((vehicle) => {
        const coord = this.getCoord(vehicle.position);
        if (coord) points.push(coord);
      });

      this.landmarkMarkers.forEach((landmark) => {
        const lat = Number(landmark?.destino_lat);
        const lng = Number(landmark?.destino_long);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push({ lat, lng });
        }
      });

      if (this.addressCoordinates) {
        const lat = Number(this.addressCoordinates.lat);
        const lng = Number(this.addressCoordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push({ lat, lng });
        }
      }

      return points;
    },

    // getCorePoints mantido caso queira usar em outro contexto,
    // mas NÃO é mais usado no fitBoundsToMarkers
    getCorePoints(points) {
      if (!points || points.length < 6) return points;

      const median = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        if (sorted.length % 2 === 0) {
          return (sorted[mid - 1] + sorted[mid]) / 2;
        }
        return sorted[mid];
      };

      const centerLat = median(points.map((p) => p.lat));
      const centerLng = median(points.map((p) => p.lng));

      const distanceScore = (point) => {
        const dLat = point.lat - centerLat;
        const dLng = point.lng - centerLng;
        return Math.sqrt(dLat * dLat + dLng * dLng);
      };

      const withDistance = points
        .map((point) => ({ point, score: distanceScore(point) }))
        .sort((a, b) => a.score - b.score);

      const keepCount = Math.max(Math.ceil(points.length * 0.8), 4);
      return withDistance.slice(0, keepCount).map((item) => item.point);
    },

    onCircleChanged() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.fitMapToCircle();
        }, 300);
      });
    },

    fitMapToCircle() {
      if (
        !this.addressCoordinates ||
        !this.normalizedRadius ||
        !this.$refs.mapRef
      ) {
        return;
      }

      this.$refs.mapRef.$mapPromise
        .then((map) => {
          const bounds = new google.maps.LatLngBounds();

          const center = new google.maps.LatLng(
            this.addressCoordinates.lat,
            this.addressCoordinates.lng
          );
          bounds.extend(center);

          const earthRadiusKm = 6371;
          const radiusInDegrees =
            (this.normalizedRadius / earthRadiusKm) * (180 / Math.PI);
          const lngCorrection = Math.cos(
            (this.addressCoordinates.lat * Math.PI) / 180
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat + radiusInDegrees,
              this.addressCoordinates.lng
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat - radiusInDegrees,
              this.addressCoordinates.lng
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat,
              this.addressCoordinates.lng + radiusInDegrees / lngCorrection
            )
          );

          bounds.extend(
            new google.maps.LatLng(
              this.addressCoordinates.lat,
              this.addressCoordinates.lng - radiusInDegrees / lngCorrection
            )
          );

          map.fitBounds(bounds, {
            top: 60,
            right: 60,
            bottom: 60,
            left: 60,
          });

          const zoomListener = google.maps.event.addListener(
            map,
            "idle",
            () => {
              const currentZoom = map.getZoom();
              if (currentZoom > 15) {
                map.setZoom(15);
              }
              google.maps.event.removeListener(zoomListener);
            }
          );
        })
        .catch((error) => {
          console.error("❌ Error fitting bounds:", error);
        });
    },

    updateMapViewForAddress() {
      if (!this.addressCoordinates) return;

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.mapRef) {
            this.$refs.mapRef.$mapPromise.then((map) => {
              if (this.normalizedRadius) {
                this.fitMapToCircle();
              } else {
                map.setCenter(this.addressCoordinates);
                map.setZoom(15);
              }
            });
          }
        }, 300);
      });
    },

    creatCenter(coord) {
      if (this.center.lat == 0 && coord && !this.addressCoordinates) {
        this.center = coord;
      }
    },

    getCoord(position) {
      if (
        position &&
        (position.lat || position.latitude || position.destino_lat) &&
        (position.lng || position.lon || position.longitude || position.destino_long)
      ) {
        const lat = parseFloat(
          position.lat || position.latitude || position.destino_lat
        );
        const lng = parseFloat(
          position.lng || position.lon || position.longitude || position.destino_long
        );
        if (!isNaN(lat) && !isNaN(lng)) {
          const coord = { lat, lng };
          this.creatCenter(coord);
          return coord;
        }
      }
      return null;
    },

    focusOnSelectedVehicle() {
      if (!this.selectedVehicleId || !this.vehicleFleetData.length) return;

      const selectedVehicle = this.vehicleFleetData.find(
        (vehicle) =>
          vehicle.vehicle?.id === this.selectedVehicleId ||
          vehicle.vehicle_id === this.selectedVehicleId
      );

      if (selectedVehicle && selectedVehicle.position) {
        const coord = this.getCoord(selectedVehicle.position);
        if (coord && this.$refs.mapRef) {
          this.$refs.mapRef.$mapPromise.then((map) => {
            map.setCenter(coord);
            map.setZoom(18);
            this.center = coord;
            this.currentZoom = 18;
          });
        }
      }
    },

    fitBoundsToMarkers() {
      if (!this.$refs.mapRef) return;

      this.$refs.mapRef.$mapPromise.then((map) => {
        const points = this.getMapPoints();

        if (!points.length) return;

        if (points.length === 1) {
          map.setCenter(points[0]);
          map.setZoom(Math.min(this.zoomMapa, 14));
          return;
        }

        const bounds = new google.maps.LatLngBounds();

        // ✅ USA TODOS os pontos — sem filtro getCorePoints
        points.forEach((coord) => {
          bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, {
            top: 60,
            right: 60,
            bottom: 60,
            left: 60,
          });

          google.maps.event.addListenerOnce(map, "idle", () => {
            const currentZoom = map.getZoom();
            // Zoom mínimo razoável para não ficar muito afastado
            if (currentZoom < 5) map.setZoom(5);
            // ✅ Sem cap máximo — fitBounds decide o zoom ideal
          });
        }
      });
    },

    updateMapView() {
      if (!this.autoFitMap) return;
      if (this.addressCoordinates && this.normalizedRadius) {
        this.fitMapToCircle();
      } else if (this.addressCoordinates) {
        this.updateMapViewForAddress();
      } else if (this.selectedVehicleId) {
        this.focusOnSelectedVehicle();
      } else {
        this.fitBoundsToMarkers();
      }
    },

    setSelectedVehicleDetails(vehicle) {
      const position = vehicle.position ?? {};
      const vehicleCoord = this.getCoord(position);

      this.markerPosition = vehicleCoord;
      this.vehicleDatalhe = vehicle;

      if (vehicleCoord && this.$refs.mapRef) {
        this.$refs.mapRef.$mapPromise.then((map) => {
          map.setCenter(vehicleCoord);
          setTimeout(() => {
            map.setZoom(18);
          }, 100);
        });
      }

      this.selectedVehicleDetails = {
        BackBattery: position.BackBattery ?? 0,
        CPRDateTime: position.CPRDateTime ?? "",
        lat: position.lat ?? 0,
        lon: position.lng ?? 0,
        speed: position.speed ?? 0,
        ignition_status: position.ignition_status ?? 0,
        port_status: position.port_status ?? 0,
        siren_status: position.siren_status ?? 0,
        device_battery_level: position.device_battery_level ?? 0,
      };

      this.showInfoWindow = null;
      this.$nextTick(() => {
        this.showInfoWindow = "vehicle";
      });
    },

    handleZoomChanged(newZoom) {
      this.currentZoom = newZoom;
      this.$emit("zoomChanged", newZoom);
    },

    init() {
      this.$emit("reload", true);
    },

    handleKeyDown(event) {
      if (event.key === "r") {
        this.init();
      }
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyDown);

    if (this.mapViewTimer) {
      clearTimeout(this.mapViewTimer);
      this.mapViewTimer = null;
    }
  },

  components: {
    RefreshCcwIcon,
    CurrentLocationMap,
    VehicleInfo,
  },
};
</script>

<style scoped>
.address-info {
  max-width: 250px;
  font-family: "Roboto", sans-serif;
}

.address-info h6 {
  color: #1976d2;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 5px;
}

.address-info .badge {
  font-size: 0.75rem;
}
</style>